/* eslint-disable react/display-name */
import React, { MouseEventHandler, useMemo } from "react";
import * as Stitches from "@stitches/react";
import { colors } from "@puzzle/theme";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { constructHref } from "@puzzle/utils";
import { reportError } from "lib/errors/errors";
import { AppLink } from "./AppLink";

export const Link = React.forwardRef<
  HTMLAnchorElement,
  Pick<React.ComponentPropsWithoutRef<"a">, "target" | "rel"> & {
    css?: Stitches.CSS;
    /** Color name to attach a '$' in front of, stitches style */
    color?: keyof typeof colors;
    hoverColor?: keyof typeof colors;
    underline?: boolean;
    external?: boolean;
    href?: string;
    onClick?: MouseEventHandler;

    // TODO Why is this suddenly needed..
    // Maybe forwardRef is unnecessary if you use ComponentProps<>?
    children: React.ReactNode;
  }
>(({ color, hoverColor, css, underline, href, external = false, children, ...props }, ref) => {
  const { company, companies } = useActiveCompany();
  const activeCompanyId = company ? company.id : null;

  const hrefWithCompanyId = useMemo(() => {
    if (!href) {
      // If the original href prop is undefined, there's nothing to construct.
      // The component will render as a button due to the !href check in AppLink.
      return undefined;
    }

    // External links should not be processed through constructHref
    if (external) {
      return href;
    }

    const parameterMap = activeCompanyId ? { companyId: activeCompanyId } : undefined;
    const { href: navHref, error: hrefError } = constructHref(
      href,
      companies?.length,
      parameterMap
    );

    if (hrefError) {
      reportError(
        `Link.tsx error calling constructHref function: ${hrefError.message}`,
        hrefError.payload
      );
      return href; // Return original href on error
    }
    return navHref;
  }, [href, activeCompanyId, external]);

  return (
    <AppLink
      href={hrefWithCompanyId}
      css={css}
      ref={ref}
      color={color}
      hoverColor={hoverColor}
      underline={underline}
      external={external}
      {...props}
    >
      {children}
    </AppLink>
  );
});
